/*
 * Amara PDP — progressive enhancement only.
 *
 * Commerce behaviour (add to cart, variant selection, cart drawer, wallet
 * buttons) stays owned by the theme's product-form.js / variant-picker.js and
 * Shopify's native payment button. Nothing here re-implements checkout logic.
 *
 * Responsibilities:
 *   1. Gallery — scroll-snap sync, thumbnails, keyboard nav, counter, lightbox.
 *   2. Sticky add to cart — reveal once the primary CTA leaves the viewport.
 *   3. Variant change — keep gallery, sticky price and sticky variant id in sync.
 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-amara-product]';
  const READY_FLAG = 'amaraProductReady';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- gallery */

  class AmaraGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
      this.currentLabel = root.querySelector('[data-gallery-current]');
      this.totalLabel = root.querySelector('[data-gallery-total]');
      this.index = 0;

      // Two vocabularies, kept apart on purpose:
      //   index    = a slide's original position (data-index), stable forever
      //   position = its place among the slides currently shown
      // Thumbs, dots and zoom data are addressed by index; scrolling and
      // counters work in positions, so hidden slides never count.
      this.visible = this.slides.map((_, index) => index);

      this.readVariantMap();

      this.bindTrack();
      this.bindThumbs();
      this.bindKeyboard();
      this.bindLightbox();

      if (this.variantMap) this.applyVariant(root.dataset.initialVariantId, false);
    }

    /* ----------------------------------------------------- variant galleries */

    readVariantMap() {
      const node = this.root.querySelector('[data-variant-gallery-map]');
      if (!node) return;

      try {
        const raw = JSON.parse(node.textContent);
        this.variantMap = {};
        Object.keys(raw).forEach((variantId) => {
          const ids = Array.isArray(raw[variantId]) ? raw[variantId] : String(raw[variantId] || '').split('|');
          this.variantMap[String(variantId)] = ids.map((id) => String(id).trim()).filter(Boolean);
        });
      } catch (error) {
        console.warn('Amara PDP: could not parse the variant gallery map.', error);
      }
    }

    positionOf(index) {
      return this.visible.indexOf(index);
    }

    isVisible(index) {
      return this.visible.includes(index);
    }

    /* Shows only the media listed for a variant. A variant with no list, or a
       list that matches none of this product's media, shows everything. */
    applyVariant(variantId, scroll = true) {
      if (!this.variantMap) return;

      const ids = this.variantMap[String(variantId)] || [];
      let allowed = new Set(ids);
      let next = this.slides
        .map((slide, index) => (allowed.has(slide.dataset.mediaId) ? index : -1))
        .filter((index) => index >= 0);

      if (!next.length) next = this.slides.map((_, index) => index);

      const changed = next.join(',') !== this.visible.join(',');
      this.visible = next;

      this.slides.forEach((slide, index) => {
        const shown = this.isVisible(index);
        slide.hidden = !shown;
        slide.setAttribute('aria-hidden', String(!shown));
      });
      this.thumbs.forEach((thumb, index) => {
        thumb.hidden = !this.isVisible(index);
      });
      this.dots.forEach((dot, index) => {
        dot.hidden = !this.isVisible(index);
      });

      if (this.totalLabel) this.totalLabel.textContent = String(this.visible.length);
      // One image on show: the desktop grid gives it the full width.
      this.root.classList.toggle('amara-gallery--single', this.visible.length === 1);
      this.paintLightboxTotal();

      if (!changed) return;

      // Land on the first slide of the new set; the variant's own featured
      // image, if any, is scrolled to by the caller right after this.
      this.setActive(this.visible[0], false);
      if (scroll) this.scrollToPosition(0, true);
    }

    /* ----------------------------------------------------------------- track */

    bindTrack() {
      if (this.slides.length < 2) return;

      // IntersectionObserver on the scroll container beats a scroll listener:
      // it survives momentum scrolling on iOS without rAF throttling.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.55) {
              this.setActive(Number(entry.target.dataset.index), false);
            }
          });
        },
        { root: this.track, threshold: [0.55] }
      );

      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    bindThumbs() {
      const go = (event) => {
        const button = event.target.closest('[data-gallery-thumb]');
        if (!button) return;
        this.scrollToPosition(this.positionOf(Number(button.dataset.index)));
      };

      const thumbsWrap = this.root.querySelector('[data-gallery-thumbs]');
      if (thumbsWrap) thumbsWrap.addEventListener('click', go);
    }

    bindKeyboard() {
      if (this.slides.length < 2) return;

      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        this.scrollToPosition(Math.max(0, this.positionOf(this.index)) + delta);
      });
    }

    setActive(index, scroll = true) {
      if (!this.isVisible(index)) return;

      this.index = index;

      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      if (this.currentLabel) this.currentLabel.textContent = String(this.positionOf(index) + 1);
      if (scroll) this.scrollToPosition(this.positionOf(index));
    }

    /* Scrolls the track to the slide at a position among the visible slides,
       wrapping at both ends. */
    scrollToPosition(position, instant = false) {
      const count = this.visible.length;
      if (!count) return;

      const slide = this.slides[this.visible[((position % count) + count) % count]];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    /* Shows the media belonging to a variant, when that media is on show. */
    showMediaId(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;

      const index = Number(slide.dataset.index);
      if (!this.isVisible(index)) return;

      this.setActive(index, false);
      this.scrollToPosition(this.positionOf(index));
    }

    /* ------------------------------------------------------------ lightbox */

    bindLightbox() {
      this.dialog = this.root.querySelector('[data-gallery-lightbox]');
      const trigger = this.root.querySelector('[data-gallery-zoom]');
      const dataNode = this.root.querySelector('[data-gallery-zoom-data]');

      if (!this.dialog || !dataNode || typeof this.dialog.showModal !== 'function') {
        if (trigger) trigger.hidden = true;
        return;
      }

      try {
        this.zoomImages = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.warn('Amara PDP: could not parse zoom data.', error);
        if (trigger) trigger.hidden = true;
        return;
      }

      this.lightboxImage = this.dialog.querySelector('[data-lightbox-image]');
      this.lightboxCounter = this.dialog.querySelector('[data-lightbox-current]');
      this.lightboxTotal = this.dialog.querySelector('[data-lightbox-total]');
      this.paintLightboxTotal();

      if (trigger) trigger.addEventListener('click', () => this.openLightbox(this.index));

      this.track.addEventListener('click', (event) => {
        // Never hijack a click on a video control or model viewer.
        if (event.target.closest('video, model-viewer, iframe')) return;

        // On the desktop grid every slide is on screen at once, so open the
        // image that was actually clicked rather than the carousel's index.
        const slide = event.target.closest('[data-gallery-slide]');
        this.openLightbox(slide ? Number(slide.dataset.index) : this.index);
      });

      this.dialog
        .querySelector('[data-lightbox-close]')
        ?.addEventListener('click', () => this.dialog.close());

      this.dialog
        .querySelector('[data-lightbox-prev]')
        ?.addEventListener('click', () => this.stepLightbox(-1));

      this.dialog
        .querySelector('[data-lightbox-next]')
        ?.addEventListener('click', () => this.stepLightbox(1));

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.stepLightbox(1);
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      });

      // Click the backdrop (i.e. the dialog element itself) to dismiss.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        this.setActive(this.lightboxIndex ?? this.index);
      });
    }

    /* Visible slides that have a zoomable image (videos and models do not). */
    zoomable() {
      return this.visible.filter((index) => this.zoomImages?.[index]);
    }

    paintLightboxTotal() {
      if (this.lightboxTotal) this.lightboxTotal.textContent = String(this.zoomable().length);
    }

    openLightbox(index) {
      if (!this.paintLightbox(index)) return;
      this.dialog.showModal();
    }

    stepLightbox(delta) {
      const images = this.zoomable();
      if (!images.length) return;

      const position = Math.max(0, images.indexOf(this.lightboxIndex ?? this.index));
      this.paintLightbox(images[(position + delta + images.length) % images.length]);
    }

    paintLightbox(index) {
      const images = this.zoomable();
      const entry = images.includes(index) ? this.zoomImages[index] : null;
      if (!entry) return false;

      this.lightboxIndex = index;
      this.lightboxImage.src = entry.src;
      this.lightboxImage.alt = entry.alt || '';
      if (this.lightboxCounter) this.lightboxCounter.textContent = String(images.indexOf(index) + 1);
      return true;
    }
  }

  /* ------------------------------------------------------------ sticky ATC */

  function initStickyAtc(section) {
    const sticky = section.querySelector('[data-amara-sticky]');
    const anchor = section.querySelector('[data-amara-atc-anchor]');
    if (!sticky || !anchor) return null;

    sticky.hidden = false;

    const setVisible = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      document.body.classList.toggle('amara-sticky-visible', visible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only reveal once the CTA has scrolled off the TOP of the viewport,
        // so the bar never covers a CTA the customer has not reached yet.
        const scrolledPast = entry.boundingClientRect.top < 0;
        setVisible(!entry.isIntersecting && scrolledPast);
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
    return sticky;
  }

  /* ----------------------------------------------------------- description */

  function initDetails(section) {
    // Nothing to wire up: <details> is native. Kept as a hook point so the
    // markup contract stays visible from the JS side.
    return section.querySelectorAll('[data-amara-details]').length;
  }

  /* ------------------------------------------------------------------ init */

  function init(section) {
    if (!section || section.dataset[READY_FLAG] === 'true') return;
    section.dataset[READY_FLAG] = 'true';

    const galleryRoot = section.querySelector('[data-amara-gallery]');
    const gallery = galleryRoot ? new AmaraGallery(galleryRoot) : null;
    const sticky = initStickyAtc(section);

    initDetails(section);

    const productId = section.dataset.productId;
    if (!productId || !window.MinimogEvents?.subscribe) return;

    window.MinimogEvents.subscribe(`${productId}__VARIANT_CHANGE`, (variant) => {
      if (!variant) return;

      section.classList.toggle('is-unavailable', !variant.available);

      // Narrow the gallery to this variant's media first, then land on its
      // featured image when that image is part of the set.
      if (gallery) {
        gallery.applyVariant(variant.id);
        if (variant.featured_media?.id) gallery.showMediaId(variant.featured_media.id);
      }

      if (!sticky) return;

      // variant-picker.js only rewrites #product-form-<section> and
      // #product-form-installment, so the sticky form is ours to keep current.
      const stickyId = sticky.querySelector('[name="id"]');
      if (stickyId) stickyId.value = variant.id;

      const stickyPrice = sticky.querySelector('[data-amara-sticky-price]');
      const formatMoney = window.Shopify?.formatMoney;
      if (stickyPrice && typeof formatMoney === 'function') {
        stickyPrice.innerHTML = formatMoney(
          variant.price,
          window.MinimogSettings?.money_format
        );
      }
    });
  }

  function scan(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) init(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
